import {
  Mesh,
  CylinderBufferGeometry,
  DynamicDrawUsage,
  Group,
  InstancedMesh,
  Matrix4,
  MeshBasicMaterial,
  Object3D,
  SphereGeometry,
  Vector3,
  MeshStandardMaterial,
} from "three";
import { GLTFLoader } from "three-stdlib";

import { PositionXYZ } from "../../lib/types/statsApi";
import { Context } from "../context";
import { calculateAlpha } from "../util/clock";
import { Frames } from "../util/frame";
import { groundLevel } from "../../lib/misc/ground";
import { SceneObjectBase } from "./object";

export class Ball extends SceneObjectBase {
  private group = new Group();

  trail = new BallTrail(50);
  pad: BallPad;

  age?: number;
  contextRef: Context;

  prevFrameNum?: number;

  constructor(context: Context) {
    super();

    const loader = new GLTFLoader().setPath(`${context.options.assetBase}`);
    loader.load("/models/rbi-ball.glb", (model) => {
      model.scene.scale.multiplyScalar(2);

      this.group.add(model.scene);
      this.group.position.set(-10, -10, -10);
      this.group.visible = true;
    });

    this.group.name = "ball";
    this.contextRef = context;
    this.pad = new BallPad(context);
  }

  get object(): Object3D {
    return this.group;
  }

  update(frames: Frames, time: number) {
    const { state } = this.contextRef;

    if (frames.current && frames.current.ballPosition) {
      this.group.visible = state.ballEnabled;

      if (this.prevFrameNum != frames.current.num) {
        this.age = 0;
      } else {
        if (this.age != undefined) this.age++;
      }

      const neighbors = frames.getNeighbours<PositionXYZ>(
        (frame) => frame.ballPosition
      );

      if (
        neighbors &&
        neighbors.prev?.time != undefined &&
        neighbors.next?.time != undefined
      ) {
        const alpha = calculateAlpha(
          [neighbors.prev.time, neighbors.next.time],
          time
        );
        if (alpha != undefined) {
          const ballPosition = new Vector3(
            neighbors.prev.neighbor.x * (1 - alpha) +
              alpha * neighbors.next.neighbor.x,
            neighbors.prev.neighbor.z * (1 - alpha) +
              alpha * neighbors.next.neighbor.z,
            -(
              neighbors.prev.neighbor.y * (1 - alpha) +
              alpha * neighbors.next.neighbor.y
            )
          );

          this.group.position.copy(ballPosition);
          this.pad.update(ballPosition);
        }
      }
    } else {
      this.group.visible = false;
      this.pad.object.visible = false;
    }

    if (this.prevFrameNum != frames.current?.num) {
      this.trail.object.visible = state.trailEnabled;
      this.trail.update(frames);
    }

    this.prevFrameNum = frames.current?.num;
  }
}

class BallTrail extends SceneObjectBase {
  material = new MeshBasicMaterial({ color: 0xbd9e4d });
  private geom = new SphereGeometry(1, 16, 8);

  private trail: InstancedMesh;
  private dummy = new Object3D();
  private tempM = new Matrix4();
  private tempP = new Vector3();
  // workaround to not show the instance that appears at 0, 0, 0
  // TODO: work out what the issue is here
  private outOfClipY = -10000;
  private frequency;
  private maxSize;

  private index = 0;

  constructor(trailLen: number, frequency = 1, maxSize = 0.1) {
    super();

    this.trail = new InstancedMesh(this.geom, this.material, trailLen);
    this.trail.instanceMatrix.setUsage(DynamicDrawUsage);
    this.trail.position.setY(this.outOfClipY);

    this.frequency = frequency;
    this.maxSize = maxSize;
  }

  get object(): Object3D {
    return this.trail;
  }

  update(frames: Frames) {
    for (const frame of [...frames.missed, frames.current]) {
      if (frame == undefined || frame.num % this.frequency) continue;

      const maxDist = this.trail.count;

      const prevIndex = (current: number) =>
        current <= 0 ? maxDist - 1 : current - 1;

      const nextIndex = (current: number) =>
        current >= maxDist - 1 ? 0 : current + 1;

      const { ballPosition } = frame;
      if (ballPosition) {
        this.dummy.position.set(
          ballPosition.x,
          ballPosition.z - this.outOfClipY,
          -ballPosition.y
        );
        this.dummy.scale.set(this.maxSize, this.maxSize, this.maxSize);
        this.dummy.updateMatrix();

        this.trail.setMatrixAt(this.index, this.dummy.matrix);
      } else {
        // TODO: do per instance visibility as described here:
        //       https://github.com/mrdoob/three.js/issues/22102#issuecomment-877004180
        this.dummy.position.set(0, this.outOfClipY, 0);
        this.dummy.updateMatrix();

        this.trail.setMatrixAt(this.index, this.dummy.matrix);
      }

      for (
        let otherIndex = prevIndex(this.index);
        otherIndex != this.index;
        otherIndex = prevIndex(otherIndex)
      ) {
        this.trail.getMatrixAt(otherIndex, this.tempM);
        this.tempP.setFromMatrixPosition(this.tempM);

        const dist =
          otherIndex > this.index
            ? maxDist - otherIndex + this.index
            : this.index - otherIndex;

        // this is just a line f(size, dist) from (0, maxDist) to (maxSize, 0)
        // where the dist is the distance from the current index, or the number of
        // updates the ball at otherIndex has seen since it was the index and
        // got it's position set
        const scale = this.maxSize - (this.maxSize / maxDist) * dist;

        this.dummy.position.copy(this.tempP);
        this.dummy.scale.set(scale, scale, scale);
        this.dummy.updateMatrix();

        this.trail.setMatrixAt(otherIndex, this.dummy.matrix);
      }

      this.trail.instanceMatrix.needsUpdate = true;

      this.index = (this.index + 1) % this.trail.count;
    }
  }
}

class BallPad extends SceneObjectBase {
  private pad: Mesh;

  material: MeshStandardMaterial;

  private maxHeight = 35;
  private contextRef: Context;

  constructor(context: Context, radius: number = 6.0, height: number = 0.05) {
    super();

    const geo = new CylinderBufferGeometry(radius, radius, height, 40);
    this.material = new MeshStandardMaterial({
      color: "white",
      transparent: true,
      opacity: 0.5,
    });

    this.pad = new Mesh(geo, this.material);
    this.pad.visible = false;

    this.contextRef = context;
  }

  get object(): Object3D {
    return this.pad;
  }

  update(ballPosition: Vector3) {
    const groundHeight = groundLevel(ballPosition.x, ballPosition.z);

    this.pad.visible = this.contextRef.state.padEnabled;
    this.pad.position.set(ballPosition.x, groundHeight, ballPosition.z);

    // 0.2 <= radiusScaling <= 1.0
    const radiusScaling = Math.max(
      0.2,
      Math.min(ballPosition.y / this.maxHeight, 1.0)
    );
    this.pad.scale.set(radiusScaling, 1, radiusScaling);
  }
}
